import { sym } from '#library/symbol.library.js';
import { getType, protoType } from '#library/type.library.js';
/**
 * Asserts if a value matches one of the provided types from the Type system.
 *
 * @param obj - The value to check
 * @param types - The list of valid Types
 * @returns True if the value matches one of the specified types
 * @example
 * ```ts
 * if (isType<string>(value, 'String', 'Number')) { ... }
 * ```
 */
export const isType = (obj, ...types) => types.includes(getType(obj));
/** Type-Guards: assert `<obj>` is of `<type>` */
export const isPrimitive = (obj) => obj === null || (typeof obj !== 'object' && typeof obj !== 'function');
export const isReference = (obj) => !isPrimitive(obj);
/** Type guard to check if a value is iterable (excludes strings) */
export const isIterable = (obj) => Symbol.iterator in Object(obj) && !isString(obj);
/** Type guard to check if a value is a string */
export const isString = (obj) => typeof obj === 'string';
/** Type guard to check if a value is a non-empty string with meaningful content */
export const isText = (obj) => typeof obj === 'string' && obj.trim().length > 0;
/** Type guard to check if a value is a finite number */
export const isNumber = (obj) => Number.isFinite(obj);
/** Regular expression matching integer digits string (`^\d+$`) */
export const RE_DIGITS = /^\d+$/;
/** Regular expression matching signed integer string (`^[+-]?[0-9]+$`) */
export const RE_INTEGER = /^[+-]?[0-9]+$/;
/** Regular expression matching BigInt literal notation (`123n` or `-123n`) */
export const RE_BIGINT_LITERAL = /^[+-]?[0-9]+n$/;
/** Regular expression matching regular expression string literal (`/.../`) */
export const RE_REGEXP_LITERAL = /^\/.*\/$/;
/** Regular expression matching HTTP or HTTPS protocol prefixes (`http://` or `https://`) */
export const RE_HTTP_URL = /^https?:\/\//i;
/** Regular expression matching file protocol prefix (`file://`) */
export const RE_FILE_URL = /^file:\/\//i;
/** Regular expression matching one or more whitespace characters (`\s+`) */
export const RE_WHITESPACE = /\s+/;
/** Global regular expression matching one or more whitespace characters (`\s+/g`) */
export const RE_WHITESPACES = /\s+/g;
/** Global regular expression matching Unicode combining diacritical marks */
export const RE_COMBINING_MARKS = /[\u0300-\u036f]/g;
/** Regular expression matching hexadecimal string */
export const RE_HEX = /^[0-9a-f]+$/i;
/** Regular expression matching 8-character hexadecimal string */
export const RE_HEX_8 = /^[0-9a-f]{8}$/i;
/** Regular expression matching Base64 string */
export const RE_BASE64 = /^[A-Za-z0-9+/]*={0,2}$/;
/** Regular expression matching Base64URL string */
export const RE_BASE64URL = /^[A-Za-z0-9_-]*$/;
/** Regular expression matching standard UUID format */
export const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** Type guard to check if a value is an integer digits string (e.g. '123') */
export const isDigits = (obj) => typeof obj === 'string' && RE_DIGITS.test(obj);
/** Type guard to check if a string is an HTTP or HTTPS URL */
export const isHttpUrl = (obj) => typeof obj === 'string' && RE_HTTP_URL.test(obj);
/** Type guard to check if a string is a file URL */
export const isFileUrl = (obj) => typeof obj === 'string' && RE_FILE_URL.test(obj);
/** Type guard to check if a value consists entirely of whitespace characters */
export const isWhitespace = (obj) => typeof obj === 'string' && obj.length > 0 && obj.trim().length === 0;
/** Type guard to check if a value is a hexadecimal string */
export const isHex = (obj) => typeof obj === 'string' && RE_HEX.test(obj);
/** Type guard to check if a value is a Base64 string */
export const isBase64 = (obj) => typeof obj === 'string' && obj.length % 4 !== 1 && (!obj.includes('=') || obj.length % 4 === 0) && RE_BASE64.test(obj);
/** Type guard to check if a value is a Base64URL string */
export const isBase64Url = (obj) => typeof obj === 'string' && obj.length % 4 !== 1 && RE_BASE64URL.test(obj);
/** Type guard to check if a value is a valid UUID string */
export const isUuid = (obj) => typeof obj === 'string' && RE_UUID.test(obj);
/**
 * Tests if a value can be safely converted to a numeric value.
 * Handles strings, numbers, and BigInts, verifying finite properties and valid formats.
 *
 * @param str - The value to test
 * @returns True if the value can be evaluated numerically
 * @example
 * ```ts
 * isNumeric('123'); // true
 * isNumeric('abc'); // false
 * ```
 */
export function isNumeric(str) {
    const type = typeof str;
    switch (type) {
        case 'number': return Number.isFinite(str);
        case 'bigint': return true;
        case 'string': {
            const val = str.trim();
            if (val.length === 0)
                return false;
            return RE_BIGINT_LITERAL.test(val) || isNumber(Number(val));
        }
        default: return false;
    }
}
/** Type guard to check if a value is a BigInt */
export const isInteger = (obj) => typeof obj === 'bigint';
export const isIntegerLike = (obj) => typeof obj === 'string' && RE_BIGINT_LITERAL.test(obj.trim());
export const isDigit = (obj) => typeof obj === 'number' ? Number.isFinite(obj) : typeof obj === 'bigint';
export const isBoolean = (obj) => typeof obj === 'boolean';
export const isArray = (obj) => Array.isArray(obj);
export const isArrayLike = (obj) => protoType(obj) === 'Object' && 'length' in obj && Object.keys(obj).every(key => key === 'length' || isNumber(Number(key)));
export const isObject = (obj) => isType(obj, 'Object');
export const isPlainObject = (obj) => {
    if (obj === null || typeof obj !== 'object')
        return false;
    const proto = Object.getPrototypeOf(obj);
    return proto === Object.prototype || proto === null;
};
export const isDate = (obj) => isType(obj, 'Date');
export const isRegExp = (obj) => isType(obj, 'RegExp');
export const isRegExpLike = (obj) => typeof obj === 'string' && RE_REGEXP_LITERAL.test(obj);
export const isSymbol = (obj) => typeof obj === 'symbol';
export const isSymbolFor = (obj) => typeof obj === 'symbol' && Symbol.keyFor(obj) !== undefined;
export const isPropertyKey = (obj) => {
    const t = typeof obj;
    return t === 'string' || t === 'number' || t === 'symbol';
};
/**
 * Keys considered unsafe for object assignment or prototype manipulation
 */
const UNSAFE_KEYS = ['__proto__', 'constructor', 'prototype'];
/**
 * Asserts if a property key is safe against prototype pollution and prototype hijacking.
 * Returns false for '__proto__', 'constructor', and 'prototype'.
 *
 * @param key - The property key to check
 * @returns True if the key is safe to assign or merge
 * @example
 * ```ts
 * isSafeKey('name'); // true
 * isSafeKey('__proto__'); // false
 * ```
 */
export const isSafeKey = (key) => !UNSAFE_KEYS.includes(key);
export const isNull = (obj) => obj === null;
export const isNullish = (obj) => obj === null || obj === undefined || isType(obj, 'Void', 'Empty');
export const isUndefined = (obj) => obj === undefined || isType(obj, 'Void', 'Empty');
export const isDefined = (obj) => obj !== null && obj !== undefined && !isNullish(obj);
export const isClass = (obj) => isType(obj, 'Class');
export const isFunction = (obj) => isType(obj, 'Function', 'AsyncFunction', 'GeneratorFunction', 'AsyncGeneratorFunction');
export const isCallable = (obj) => typeof obj === 'function';
export const isPromise = (obj) => isType(obj, 'Promise');
export const isMap = (obj) => isType(obj, 'Map');
export const isSet = (obj) => isType(obj, 'Set');
export const isError = (err, constructor) => isType(err, 'Error') && (constructor ? err instanceof constructor : true);
export const isTemporal = (obj) => protoType(obj).startsWith('Temporal.') || (isDefined(globalThis.Temporal) && (obj instanceof globalThis.Temporal.Instant ||
    obj instanceof globalThis.Temporal.ZonedDateTime ||
    obj instanceof globalThis.Temporal.PlainDate ||
    obj instanceof globalThis.Temporal.PlainTime ||
    obj instanceof globalThis.Temporal.PlainDateTime ||
    obj instanceof globalThis.Temporal.Duration ||
    obj instanceof globalThis.Temporal.PlainYearMonth ||
    obj instanceof globalThis.Temporal.PlainMonthDay));
export const isInstant = (obj) => isType(obj, 'Temporal.Instant') || (isDefined(globalThis.Temporal?.Instant) && obj instanceof globalThis.Temporal.Instant) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.Instant') || (isDefined(obj) && isFunction(obj.toZonedDateTimeISO) && isUndefined(obj.timeZoneId) && isUndefined(obj.timeZone));
export const isZonedDateTime = (obj) => isType(obj, 'Temporal.ZonedDateTime') || (isDefined(globalThis.Temporal?.ZonedDateTime) && obj instanceof globalThis.Temporal.ZonedDateTime) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.ZonedDateTime') || (isDefined(obj) && isFunction(obj.toInstant) && (isDefined(obj.timeZoneId) || isDefined(obj.timeZone)));
export const isPlainDate = (obj) => isType(obj, 'Temporal.PlainDate') || (isDefined(globalThis.Temporal?.PlainDate) && obj instanceof globalThis.Temporal.PlainDate) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.PlainDate') || (isDefined(obj) && isFunction(obj.toZonedDateTime) && isUndefined(obj.timeZoneId) && isUndefined(obj.timeZone) && isDefined(obj.daysInMonth) && isUndefined(obj.hour) && isUndefined(obj.minute) && isUndefined(obj.second) && isUndefined(obj.nanosecond));
export const isPlainTime = (obj) => isType(obj, 'Temporal.PlainTime') || (isDefined(globalThis.Temporal?.PlainTime) && obj instanceof globalThis.Temporal.PlainTime) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.PlainTime') || (isDefined(obj) && isFunction(obj.toPlainDateTime) && isUndefined(obj.daysInMonth));
export const isPlainDateTime = (obj) => isType(obj, 'Temporal.PlainDateTime') || (isDefined(globalThis.Temporal?.PlainDateTime) && obj instanceof globalThis.Temporal.PlainDateTime) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.PlainDateTime') || (isDefined(obj) && isFunction(obj.toZonedDateTime) && isUndefined(obj.timeZoneId) && isUndefined(obj.timeZone) && (isDefined(obj.hour) || isDefined(obj.minute) || isDefined(obj.second) || isDefined(obj.nanosecond)));
export const isDuration = (obj) => isType(obj, 'Temporal.Duration') || (isDefined(globalThis.Temporal?.Duration) && obj instanceof globalThis.Temporal.Duration) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.Duration');
export const isDurationLike = (obj) => isString(obj) || isDuration(obj) || (isObject(obj) && ('years' in obj || 'months' in obj || 'weeks' in obj || 'days' in obj ||
    'hours' in obj || 'minutes' in obj || 'seconds' in obj ||
    'milliseconds' in obj || 'microseconds' in obj || 'nanoseconds' in obj ||
    'yy' in obj || 'mm' in obj || 'ww' in obj || 'dd' in obj ||
    'hh' in obj || 'mi' in obj || 'ss' in obj ||
    'ms' in obj || 'us' in obj || 'ns' in obj));
export const isZonedDateTimeLike = (obj) => isString(obj) || isZonedDateTime(obj) || (isObject(obj) && ('year' in obj || 'month' in obj || 'day' in obj || 'hour' in obj || 'minute' in obj || 'second' in obj ||
    'millisecond' in obj || 'microsecond' in obj || 'nanosecond' in obj || 'monthCode' in obj || 'offset' in obj || 'timeZone' in obj || 'calendar' in obj));
export const isPlainYearMonth = (obj) => isType(obj, 'Temporal.PlainYearMonth') || (isDefined(globalThis.Temporal?.PlainYearMonth) && obj instanceof globalThis.Temporal.PlainYearMonth);
export const isPlainMonthDay = (obj) => isType(obj, 'Temporal.PlainMonthDay') || (isDefined(globalThis.Temporal?.PlainMonthDay) && obj instanceof globalThis.Temporal.PlainMonthDay);
/** Type guard to check if a value is an Intl.Locale instance */
export const isLocale = (obj) => typeof Intl !== 'undefined' && typeof Intl.Locale === 'function' && obj instanceof Intl.Locale;
// non-standard Objects
export const isEnum = (obj) => isType(obj, 'Enumify');
export const isPledge = (obj) => isType(obj, 'Pledge');
/** assert value for secure() */
export const isExtensible = (obj) => isDefined(obj?.[sym.$Extensible]);
export const isTarget = (obj) => isDefined(obj?.[sym.$Target]);
/** Type guard to check if a value is an AbortSignal (cross-realm safe) */
export const isAbortSignal = (obj) => isReference(obj) &&
    'aborted' in obj &&
    isCallable(obj.addEventListener) &&
    isCallable(obj.removeEventListener);
/** Type guard to check if a value is an AbortController */
export const isAbortController = (obj) => isReference(obj) && 'signal' in obj && isCallable(obj.abort);
/**
 * Checks if a value is effectively empty.
 * Returns true for nullish values, empty objects, empty strings, NaN, empty arrays,
 * empty sets/maps, empty typed arrays/buffers, and invalid dates.
 *
 * @param obj - The value to check
 * @returns True if the value is empty
 * @example
 * ```ts
 * isEmpty([]); // true
 * isEmpty({ a: 1 }); // false
 * ```
 */
export const isEmpty = (obj) => false
    || isNullish(obj)
    || (isObject(obj) && Reflect.ownKeys(obj).length === 0)
    || (isString(obj) && obj.trim().length === 0)
    || (typeof obj === 'number' && Number.isNaN(obj))
    || (isArray(obj) && obj.length === 0)
    || (isSet(obj) && obj.size === 0)
    || (isMap(obj) && obj.size === 0)
    || (ArrayBuffer.isView(obj) && obj.byteLength === 0)
    || (isDate(obj) && Number.isNaN(obj.getTime()));
/**
 * Asserts a condition is true, otherwise throws an Error.
 *
 * @param condition - The boolean condition that must be true
 * @param message - The error message to throw if the condition is false
 * @throws {Error} If the condition evaluates to false
 * @example
 * ```ts
 * assertCondition(user.isLoggedIn, 'User must be logged in');
 * ```
 */
export function assertCondition(condition, message) {
    if (!condition)
        throw new Error(message);
}
/**
 * Asserts a value is a string, otherwise throws an Error.
 *
 * @param str - The value to assert as a string
 * @throws {Error} If the value is not a string
 */
export function assertString(str) { assertCondition(isString(str), `Invalid string: ${str}`); }
;
/**
 * A TypeScript exhaustiveness check that throws an error if reached at runtime.
 *
 * @param val - The value that should never exist
 * @throws {Error} Always throws an error
 */
export function assertNever(val) { throw new Error(`Unexpected object: ${val}`); }
;
